import React, { useEffect, useRef, useState } from "react";
import { useModalBackdropClose } from "../hooks/useModalBackdropClose";

export interface SMRTTasksAddTypeModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  initialName?: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void> | void;
}

export default function SMRTTasksAddTypeModal({
  isOpen,
  mode,
  initialName = "",
  onClose,
  onSubmit,
}: SMRTTasksAddTypeModalProps) {
  const [name, setName] = useState<string>(initialName);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(initialName);
    setError(null);
    setSaving(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen, initialName]);

  // Hook must come before the early return so the order stays stable.
  const backdrop = useModalBackdropClose(() => {
    if (saving) return;
    onClose();
  });

  if (!isOpen) return null;

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSubmit(trimmed);
    } catch (err: unknown) {
      setError((err as Error)?.message || "Failed to save");
      setSaving(false);
    }
  };

  const title = mode === "create" ? "Add Task Type" : "Edit Task Type";

  return (
    <div className="smrttasks-modal-overlay" {...backdrop}>
      <div className="smrttasks-modal" onClick={(e) => e.stopPropagation()}>
        <div className="smrttasks-modal-header">
          <h2>{title}</h2>
        </div>
        <div className="smrttasks-modal-body">
          {error && <p className="smrttasks-modal-error">{error}</p>}

          <div className="smrttasks-form-row">
            <label htmlFor="smrttasks-name-input">Name</label>
            <input
              id="smrttasks-name-input"
              ref={inputRef}
              type="text"
              className="smrttasks-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") handleClose();
              }}
              disabled={saving}
              maxLength={120}
              placeholder="e.g. Inspection"
            />
          </div>

          <div className="smrttasks-modal-actions">
            <button
              type="button"
              className="smrttasks-btn-cancel"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="smrttasks-btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
